import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Send, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div data-testid={`star-rating-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <p className="text-sm font-medium text-slate-700 mb-2">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(s => (
          <button
            key={s}
            type="button"
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(s)}
            className="transition-transform hover:scale-110"
            data-testid={`star-${label.replace(/\s/g, '-').toLowerCase()}-${s}`}
          >
            <Star
              className={`h-8 w-8 transition-colors ${
                s <= (hover || value)
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SurveyForm({ propertyId, analysisId, onClose }) {
  const [starsPrice, setStarsPrice] = useState(0);
  const [starsTool, setStarsTool] = useState(0);
  const [text, setText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [suggestions, setSuggestions] = useState({ tags: [], recent_texts: [] });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/surveys/suggestions`)
      .then(r => r.json())
      .then(data => setSuggestions(data))
      .catch(() => {});
  }, []);

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (starsPrice === 0 || starsTool === 0) {
      toast.error('Bitte vergeben Sie mindestens je 1 Stern');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/surveys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: propertyId,
          analysis_id: analysisId,
          stars_price: starsPrice,
          stars_tool: starsTool,
          improvement_text: text,
          tags: selectedTags,
        }),
      });
      setSubmitted(true);
      toast.success('Vielen Dank für Ihr Feedback!');
    } catch {
      toast.error('Fehler beim Senden');
    }
    setSubmitting(false);
  };

  if (dismissed) return null;

  if (submitted) {
    return (
      <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="survey-success">
        <CardContent className="p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Star className="h-7 w-7 text-emerald-600 fill-emerald-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Vielen Dank!</h3>
          <p className="text-muted-foreground">Ihr Feedback hilft uns, das Tool weiter zu verbessern.</p>
        </CardContent>
      </Card>
    );
  }

  const defaultTags = [
    'Benutzerfreundlich', 'Schnelle Berechnung', 'Preis angemessen',
    'PDF hilfreich', 'Mehr Details gewünscht', 'Design modern',
    'Einfach zu bedienen', 'Verbesserungsbedarf'
  ];

  const allTags = [
    ...new Set([
      ...suggestions.tags.map(t => t.tag),
      ...defaultTags,
    ])
  ];

  return (
    <Card className="border-2 border-slate-200 overflow-hidden" data-testid="survey-form">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-white/10">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold">Ihr Feedback</p>
            <p className="text-xs text-slate-300">Helfen Sie uns, dieses Tool zu verbessern</p>
          </div>
        </div>
        <button onClick={() => setDismissed(true)} className="text-slate-400 hover:text-white transition-colors" data-testid="survey-dismiss">
          <X className="h-5 w-5" />
        </button>
      </div>
      <CardContent className="p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <StarRating
            value={starsPrice}
            onChange={setStarsPrice}
            label="Wie fanden Sie die Preiskalkulation?"
          />
          <StarRating
            value={starsTool}
            onChange={setStarsTool}
            label="Wie bewerten Sie dieses Tool?"
          />
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-3">Was trifft zu? (optional)</p>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selectedTags.includes(tag)
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
                data-testid={`survey-tag-${tag.replace(/\s/g, '-').toLowerCase()}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Verbesserungsvorschläge (optional)</p>
          {suggestions.recent_texts.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {suggestions.recent_texts.slice(0, 5).map((t, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="cursor-pointer hover:bg-slate-100 text-xs max-w-[200px] truncate"
                  onClick={() => setText(t)}
                  title={t}
                  data-testid={`survey-suggestion-${i}`}
                >
                  {t.length > 40 ? t.slice(0, 40) + '...' : t}
                </Badge>
              ))}
            </div>
          )}
          <textarea
            className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
            rows={3}
            placeholder="Was können wir verbessern?"
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={500}
            data-testid="survey-text-input"
          />
          <p className="text-xs text-muted-foreground text-right mt-1">{text.length}/500</p>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setDismissed(true)}
            className="text-sm text-muted-foreground hover:text-slate-700 transition-colors"
            data-testid="survey-skip"
          >
            Überspringen
          </button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || starsPrice === 0 || starsTool === 0}
            className="gap-2"
            data-testid="survey-submit"
          >
            <Send className="h-4 w-4" />
            {submitting ? 'Senden...' : 'Feedback absenden'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
